import React, { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar as CalendarWidget } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { format } from "date-fns";
import {
  Calendar, CalendarRange, CalendarIcon, ArrowRight, X, Filter, Lock, Download,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MONTH_NAMES = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const fmtMonth = (m) => { if (!m) return ""; const [y, mo] = m.split("-"); return `${MONTH_NAMES[parseInt(mo) - 1] || mo} ${y}`; };

const DashboardFilters = ({ onFiltersChange, onGranularityChange, dark }) => {
  const [options, setOptions] = useState({ task_types: [], resources: [], languages: [], statuses: [], months: [] });

  // Filter state
  const [dateMode, setDateMode] = useState("all"); // all | single | range | dates
  const [month, setMonth] = useState("");
  const [fromMonth, setFromMonth] = useState("");
  const [toMonth, setToMonth] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [taskType, setTaskType] = useState("");
  const [resource, setResource] = useState("");
  const [language, setLanguage] = useState("");
  const [taskStatus, setTaskStatus] = useState("");
  const [lockedOnly, setLockedOnly] = useState(false);
  const [granularity, setGranularity] = useState("all_time"); // all_time | monthly | weekly | daily

  useEffect(() => {
    axios.get(`${API}/dashboard/filter-options`).then(r => setOptions(r.data)).catch(() => {});
  }, []);

  // Compute auto date range based on granularity
  const getGranularityDates = (g) => {
    const now = new Date();
    const fmt = (d) => d.toISOString().split("T")[0];
    if (g === "daily") {
      return { from_date: fmt(now), to_date: fmt(now) };
    }
    if (g === "weekly") {
      const day = now.getDay();
      const mon = new Date(now);
      mon.setDate(now.getDate() - (day === 0 ? 6 : day - 1)); // Monday
      const sun = new Date(mon);
      sun.setDate(mon.getDate() + 6); // Sunday
      return { from_date: fmt(mon), to_date: fmt(sun) };
    }
    if (g === "monthly") {
      const first = new Date(now.getFullYear(), now.getMonth(), 1);
      const last = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      return { from_date: fmt(first), to_date: fmt(last) };
    }
    return {};
  };

  // Emit filters whenever they change
  useEffect(() => {
    const params = {};

    // Granularity auto-sets date range (overrides manual date modes)
    if (granularity !== "all_time") {
      const gDates = getGranularityDates(granularity);
      if (gDates.from_date) params.from_date = gDates.from_date;
      if (gDates.to_date) params.to_date = gDates.to_date;
    }

    // Manual date modes only apply when granularity is "all_time" (default monthly)
    if (granularity === "all_time") {
      if (dateMode === "single" && month) params.month = month;
      if (dateMode === "range" && fromMonth && toMonth) { params.from_month = fromMonth; params.to_month = toMonth; }
      if (dateMode === "dates" && fromDate && toDate) { params.from_date = fromDate; params.to_date = toDate; }
    }

    if (taskType) params.task_type = taskType;
    if (resource) params.resource = resource;
    if (language) params.language = language;
    if (taskStatus) params.task_status = taskStatus;
    if (lockedOnly) params.locked_only = "true";
    onFiltersChange(params);
  }, [dateMode, month, fromMonth, toMonth, fromDate, toDate, taskType, resource, language, taskStatus, lockedOnly, granularity]);

  const hasFilters = dateMode !== "all" || taskType || resource || language || taskStatus || lockedOnly || granularity !== "all_time";
  const clearAll = () => { setDateMode("all"); setMonth(""); setFromMonth(""); setToMonth(""); setFromDate(""); setToDate(""); setTaskType(""); setResource(""); setLanguage(""); setTaskStatus(""); setLockedOnly(false); setGranularity("all_time"); if (onGranularityChange) onGranularityChange("all_time"); };

  const activeCount = [dateMode !== "all" ? 1 : 0, taskType ? 1 : 0, resource ? 1 : 0, language ? 1 : 0, taskStatus ? 1 : 0, lockedOnly ? 1 : 0, granularity !== "all_time" ? 1 : 0].reduce((a, b) => a + b, 0);

  return (
    <div className={`border rounded-xl p-4 ${dark ? "bg-[#12151e] border-[#1e2231]" : "bg-white border-slate-200"}`} data-testid="dashboard-filters">
      {/* Date mode tabs + filters row */}
      <div className="flex items-center gap-3 flex-wrap">
        {/* Date mode toggle */}
        <div className={`flex items-center gap-0.5 rounded-lg p-0.5 ${dark ? "bg-[#0b0e14]" : "bg-slate-100"}`}>
          {[
            { key: "all", label: "All Time" },
            { key: "single", label: "Single Month", icon: Calendar },
            { key: "range", label: "Month Range", icon: CalendarRange },
            { key: "dates", label: "Due Date Range", icon: CalendarIcon },
          ].map(m => (
            <button key={m.key} onClick={() => setDateMode(m.key)}
              className={`flex items-center gap-1 px-2.5 py-1.5 rounded-md text-[11px] font-medium transition-all ${dateMode === m.key ? (dark ? "bg-[#1e2231] text-blue-400 shadow-sm" : "bg-white text-indigo-700 shadow-sm") : (dark ? "text-slate-500 hover:text-slate-300" : "text-slate-500 hover:text-slate-700")}`}
              data-testid={`filter-mode-${m.key}`}>
              {m.icon && <m.icon className="w-3 h-3" />} {m.label}
            </button>
          ))}
        </div>

        {/* Date controls */}
        {dateMode === "single" && (
          <Select value={month || "none"} onValueChange={v => setMonth(v === "none" ? "" : v)}>
            <SelectTrigger className={`h-8 text-[11px] w-44 ${dark ? "bg-[#0b0e14] border-[#1e2231] text-slate-300" : ""}`} data-testid="filter-month"><SelectValue placeholder="Select month..." /></SelectTrigger>
            <SelectContent className="max-h-64">
              <SelectItem value="none">Select month...</SelectItem>
              {options.months.map(m => <SelectItem key={m} value={m}>{fmtMonth(m)}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        {dateMode === "range" && (<>
          <Select value={fromMonth || "none"} onValueChange={v => setFromMonth(v === "none" ? "" : v)}>
            <SelectTrigger className={`h-8 text-[11px] w-40 ${dark ? "bg-[#0b0e14] border-[#1e2231] text-slate-300" : ""}`} data-testid="filter-from-month"><SelectValue placeholder="From..." /></SelectTrigger>
            <SelectContent className="max-h-64">{options.months.map(m => <SelectItem key={m} value={m}>{fmtMonth(m)}</SelectItem>)}</SelectContent>
          </Select>
          <ArrowRight className="w-3.5 h-3.5 text-slate-400" />
          <Select value={toMonth || "none"} onValueChange={v => setToMonth(v === "none" ? "" : v)}>
            <SelectTrigger className={`h-8 text-[11px] w-40 ${dark ? "bg-[#0b0e14] border-[#1e2231] text-slate-300" : ""}`} data-testid="filter-to-month"><SelectValue placeholder="To..." /></SelectTrigger>
            <SelectContent className="max-h-64">{options.months.map(m => <SelectItem key={m} value={m}>{fmtMonth(m)}</SelectItem>)}</SelectContent>
          </Select>
        </>)}
        {dateMode === "dates" && (<>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className={`h-8 w-36 justify-start text-[11px] font-normal ${fromDate ? (dark ? "text-slate-200" : "text-slate-700") : (dark ? "text-slate-500" : "text-slate-400")} ${dark ? "bg-[#0b0e14] border-[#1e2231]" : ""}`} data-testid="filter-from-date">
                <CalendarIcon className="w-3 h-3 mr-1 text-slate-400" />{fromDate ? format(new Date(fromDate + "T00:00:00"), "dd MMM yyyy") : "From date"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start"><CalendarWidget mode="single" selected={fromDate ? new Date(fromDate + "T00:00:00") : undefined} onSelect={d => setFromDate(d ? format(d, "yyyy-MM-dd") : "")} initialFocus /></PopoverContent>
          </Popover>
          <ArrowRight className="w-3.5 h-3.5 text-slate-400" />
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className={`h-8 w-36 justify-start text-[11px] font-normal ${toDate ? (dark ? "text-slate-200" : "text-slate-700") : (dark ? "text-slate-500" : "text-slate-400")} ${dark ? "bg-[#0b0e14] border-[#1e2231]" : ""}`} data-testid="filter-to-date">
                <CalendarIcon className="w-3 h-3 mr-1 text-slate-400" />{toDate ? format(new Date(toDate + "T00:00:00"), "dd MMM yyyy") : "To date"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start"><CalendarWidget mode="single" selected={toDate ? new Date(toDate + "T00:00:00") : undefined} onSelect={d => setToDate(d ? format(d, "yyyy-MM-dd") : "")} initialFocus /></PopoverContent>
          </Popover>
        </>)}

        <div className={`w-px h-6 ${dark ? "bg-[#1e2231]" : "bg-slate-200"}`} />

        {/* Other filters */}
        <Select value={taskType || "all"} onValueChange={v => setTaskType(v === "all" ? "" : v)}>
          <SelectTrigger className={`h-8 text-[11px] w-32 ${dark ? "bg-[#0b0e14] border-[#1e2231] text-slate-300" : ""}`} data-testid="filter-task-type"><SelectValue placeholder="All Tasks" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Tasks</SelectItem>
            {options.task_types.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={resource || "all"} onValueChange={v => setResource(v === "all" ? "" : v)}>
          <SelectTrigger className={`h-8 text-[11px] w-40 ${dark ? "bg-[#0b0e14] border-[#1e2231] text-slate-300" : ""}`} data-testid="filter-resource"><SelectValue placeholder="All Resources" /></SelectTrigger>
          <SelectContent className="max-h-64">
            <SelectItem value="all">All Resources</SelectItem>
            {options.resources.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={language || "all"} onValueChange={v => setLanguage(v === "all" ? "" : v)}>
          <SelectTrigger className={`h-8 text-[11px] w-32 ${dark ? "bg-[#0b0e14] border-[#1e2231] text-slate-300" : ""}`} data-testid="filter-language"><SelectValue placeholder="All Languages" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Languages</SelectItem>
            {options.languages.map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={taskStatus || "all"} onValueChange={v => setTaskStatus(v === "all" ? "" : v)}>
          <SelectTrigger className={`h-8 text-[11px] w-32 ${dark ? "bg-[#0b0e14] border-[#1e2231] text-slate-300" : ""}`} data-testid="filter-status"><SelectValue placeholder="All Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            {options.statuses.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>

        <div className={`w-px h-6 ${dark ? "bg-[#1e2231]" : "bg-slate-200"}`} />

        {/* Locked Batches Toggle */}
        <Button
          variant={lockedOnly ? "default" : "outline"}
          size="sm"
          className={`h-8 text-[11px] gap-1.5 ${lockedOnly ? "bg-emerald-600 hover:bg-emerald-700 text-white" : (dark ? "text-slate-400 border-[#1e2231]" : "text-slate-600")}`}
          onClick={() => setLockedOnly(!lockedOnly)}
          data-testid="filter-locked-only"
        >
          <Lock className="w-3 h-3" />
          Locked Batches
        </Button>

        <div className={`w-px h-6 ${dark ? "bg-[#1e2231]" : "bg-slate-200"}`} />

        {/* Trend Granularity */}
        <div className={`flex items-center gap-0.5 rounded-md p-0.5 ${dark ? "bg-[#0b0e14]" : "bg-slate-100"}`}>
          {[
            { key: "all_time", label: "All Time" },
            { key: "monthly", label: "This Month" },
            { key: "weekly", label: "This Week" },
            { key: "daily", label: "Today" },
          ].map(g => (
            <button key={g.key} onClick={() => { setGranularity(g.key); if (onGranularityChange) onGranularityChange(g.key); }}
              className={`px-2 py-1 rounded text-[10px] font-medium transition-all ${granularity === g.key ? (dark ? "bg-[#1e2231] text-blue-400 shadow-sm" : "bg-white text-slate-800 shadow-sm") : (dark ? "text-slate-500 hover:text-slate-300" : "text-slate-500 hover:text-slate-700")}`}
              data-testid={`granularity-${g.key}`}>
              {g.label}
            </button>
          ))}
        </div>

        <div className={`w-px h-6 ${dark ? "bg-[#1e2231]" : "bg-slate-200"}`} />

        {/* Download PDF Report */}
        <Button variant="outline" size="sm" className={`h-8 text-[11px] gap-1.5 ${dark ? "text-slate-400 border-[#1e2231]" : "text-slate-600"}`} data-testid="filter-download-pdf"
          onClick={() => {
            const params = new URLSearchParams();
            if (dateMode === "single" && month) params.set("month", month);
            if (dateMode === "range" && fromMonth && toMonth) { params.set("from_month", fromMonth); params.set("to_month", toMonth); }
            if (dateMode === "dates" && fromDate && toDate) { params.set("from_date", fromDate); params.set("to_date", toDate); }
            if (taskType) params.set("task_type", taskType);
            if (resource) params.set("resource", resource);
            if (language) params.set("language", language);
            if (taskStatus) params.set("task_status", taskStatus);
            if (lockedOnly) params.set("locked_only", "true");
            window.open(`${API}/dashboard/export-pdf?${params}`, "_blank");
          }}>
          <Download className="w-3 h-3" /> PDF
        </Button>

        {hasFilters && (
          <Button variant="ghost" size="sm" className="h-8 text-[11px] text-slate-500 px-2" onClick={clearAll} data-testid="filter-clear-all">
            <X className="w-3 h-3 mr-0.5" /> Clear All
            {activeCount > 0 && <Badge className="ml-1 bg-indigo-100 text-indigo-600 border-0 text-[9px]">{activeCount}</Badge>}
          </Button>
        )}
      </div>
    </div>
  );
};

export default DashboardFilters;
